import {ERROR, SIGN_IN, SIGNED_IN, LOADING, LOGGED_OUT} from "module/constants";
import common from "module/common";
import socket from 'module/socket';
import {fetchAuthenticatedUser} from "./authPayload";

let googleAuth = null;

const DOMAINS = ['projectestimator.services', 'pes.ltd', 'easypricepro.com', 'buildaviator.services'];
const CLIENT_ID_LIVE = '233047984860-13airm73c8fsso2m883abi7jnaf29scv.apps.googleusercontent.com';
const CLIENT_ID_TEST = '233047984860-cif048qkbo37jbtlv3efakuk0seqad4c.apps.googleusercontent.com';

function getGoogleSignInElement() {
  return document.querySelector('.sign-in-container .google-sign-in')
    || document.querySelector('.signInContainer .signInButton');
}

function renderGoogleButton(dispatch) {
  window.requestAnimationFrame(() => {
    const element = getGoogleSignInElement();
    dispatch('renderButton', element);
  });
}

function getGoogleIdentityService() {
  return window.google?.accounts?.id || null;
}

function initialiseGoogleIdentity(commit, dispatch, autoSelect = true) {
  const googleIdentity = getGoogleIdentityService();

  if (!googleIdentity) {
    commit(SET_STATE, ERROR);
    commit(SET_STATE_REASON, 'Google sign-in is still loading. Please try again.');
    return null;
  }

  googleIdentity.initialize({
    client_id: process.env.NODE_ENV === 'production' ? CLIENT_ID_LIVE : CLIENT_ID_TEST,
    itp_support: true,
    auto_select: autoSelect,
    cancel_on_tap_outside: false,
    callback: (response) => {
      commit(SET_STATE, LOADING);
      dispatch('verify', response.credential);
      googleAuth = response.credential;
    }
  });

  return googleIdentity;
}

function revokeGoogleAuth() {
  const googleIdentity = getGoogleIdentityService();

  if (!googleIdentity || !googleAuth) {
    googleAuth = null;
    return;
  }

  try {
    googleIdentity.revoke(common.parseJwt(googleAuth).email, () => {
      googleAuth = null;
    });
  } catch (error) {
    googleAuth = null;
  }
}

const LOGGED_IN_USER = 'loggedInUser';

const SET_STATE = 'loggedInState';
const SET_STATE_REASON = 'loggedInStateReason';

const SET_LOGGED_IN_USER = 'loggedInUser';
const SET_ALL_USERS = 'allUsers';

let signInAttemptNumber = 0;

export const actions = {
  initGoogle({commit, dispatch, getters, rootGetters}) {
    const isSafari = /constructor/i.test(window.HTMLElement) || (function (p) {
      return p.toString() === "[object SafariRemoteNotification]";
    })(!window['safari'] || (typeof safari !== 'undefined' && window['safari'].pushNotification));

    if (isSafari) {
      commit(SET_STATE, SIGN_IN);
      return;
    }

    const googleIdentity = initialiseGoogleIdentity(commit, dispatch, true);

    if (!googleIdentity) {
      return;
    }

    renderGoogleButton(dispatch);
  },
  verify({commit, dispatch}, user) {
    if (user === null) {
      return;
    }

    const decodedUser = common.parseJwt(user);
    if (DOMAINS.indexOf(decodedUser.hd) <= -1) {
      dispatch('logoutUserWithGoogle');

      commit(SET_STATE, ERROR);
      commit(SET_STATE_REASON, 'Google Account Not Supported Please Use Email Login');
      return;
    }

    signInAttemptNumber++;

    fetchAuthenticatedUser({data: {idToken: user}, useForm: true})
        .then((loggedInUser) => {
          signInAttemptNumber = 0;
          commit(SET_LOGGED_IN_USER, loggedInUser);
          commit(SET_STATE, SIGNED_IN);
          dispatch('brand/load', null, {root: true});
          socket.validate(user);
        }, (error) => {
          if (_.get(error, 'data.message', null) === 'Expired or Invalid Session' && signInAttemptNumber <= 2) {
            dispatch('refreshAndVerify');
          } else {
            commit(SET_STATE, ERROR);
            commit(SET_STATE_REASON, _.get(error, 'data.message', null));
            revokeGoogleAuth();
          }
        });
  },
  renderButton({commit, dispatch}, el) {
    if (!el) {
      return;
    }

    const googleIdentity = getGoogleIdentityService();

    if (!googleIdentity) {
      return;
    }

    el.innerHTML = '';

    googleIdentity.renderButton(
        el,
        {theme: 'outline', size: 'large', shape: 'pill', width: 240}  // customization attributes
    );
    googleIdentity.prompt()
  },
  logoutUserWithGoogle({commit, dispatch}) {
    const googleIdentity = getGoogleIdentityService();

    signInAttemptNumber = 0;
    revokeGoogleAuth();

    if (!googleIdentity) {
      return;
    }

    commit(SET_STATE_REASON, 'User logged out');
    googleIdentity.disableAutoSelect();

    const reinitialisedGoogleIdentity = initialiseGoogleIdentity(commit, dispatch, false);

    if (!reinitialisedGoogleIdentity) {
      return;
    }

    renderGoogleButton(dispatch);
  },
  refreshAndVerify({commit, dispatch}) {
    try {
      signInAttemptNumber = 0;
      commit(SET_STATE_REASON, 'Your session expired. Please sign in again.');
      commit(SET_STATE, SIGN_IN);

      const googleIdentity = initialiseGoogleIdentity(commit, dispatch, true);

      if (!googleIdentity) {
        return;
      }

      renderGoogleButton(dispatch);
    } catch (error) {
      commit(SET_STATE, ERROR);
      commit(SET_STATE_REASON, _.get(error, 'data.message', null));
      revokeGoogleAuth();
    }
  }
};

export const getters = {
  hasGoogleAuth: () => {
    return _.isEmpty(googleAuth) === false;
  },
  idToken: (state) => () => {
    let idToken = null;

    if (googleAuth && state[LOGGED_IN_USER].state.state === SIGNED_IN) {
      idToken = googleAuth;
    }

    return _.isEmpty(idToken) === false ? idToken : null;
  },
};
